import React from 'react';
import { NavLink } from 'react-router-dom';
import { 
    FaChartLine, FaClipboardList, FaFileInvoice, FaEuroSign, 
    FaUsers, FaCog, FaSignOutAlt, FaUserShield, FaMoneyBillWave,
    FaBook, FaFileInvoiceDollar
} from 'react-icons/fa';

const Sidebar = ({ user, onLogout }) => {
    
    const getInitials = (name) => {
        return name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U';
    };

    const userEmail = user?.email || user?.username || '';
    const isSuperAdmin = user?.role === 'superadmin'; 
    const isAdmin = user?.role === 'admin' || isSuperAdmin; 
    const isDirection = user?.role === 'direction'; 

    const isKbOuattara = userEmail === 'kb.ouattara@arsn.ci' || userEmail === 'kb.ouattara@gmail.com';
    const isFcGadie = userEmail === 'fc.gadie@arsn.ci' || userEmail === 'fc.gadie@gmail.com';

    const canManageFacturation = !isDirection && (isAdmin || isKbOuattara || isFcGadie || user?.role === 'facturation');
    const canManageEncaissement = !isDirection && (isAdmin || isKbOuattara || isFcGadie || user?.role === 'finance');
    const canViewTresorerie = !isDirection && (isAdmin || isFcGadie || isKbOuattara || user?.role === 'finance');

    let roleLabel = 'Gestionnaire';
    if (isSuperAdmin) roleLabel = 'Super Administrateur';
    else if (isAdmin) roleLabel = 'Administrateur';
    else if (isDirection) roleLabel = 'Direction Générale';
    else if (isKbOuattara) roleLabel = 'Facturation & Caisse';
    else if (isFcGadie) roleLabel = 'Finance & Facturation';

    return (
        <>
            <style>
                {`
                    .custom-sidebar { 
                        background: rgba(10, 20, 12, 0.75); 
                        backdrop-filter: blur(12px); 
                        -webkit-backdrop-filter: blur(12px);
                        border-right: 1px solid rgba(153, 255, 0, 0.15); 
                        box-shadow: 2px 0 15px rgba(0,0,0,0.8); 
                        z-index: 1000; 
                        transition: width 0.3s ease; 
                    }
                    .nav-custom-link { 
                        color: #88b58a; 
                        border-radius: 6px; 
                        padding: 12px 16px; 
                        font-size: 0.9rem; 
                        font-weight: 600; 
                        text-decoration: none; 
                        display: flex; 
                        align-items: center; 
                        transition: all 0.2s ease-in-out; 
                        margin-bottom: 4px; 
                        border-left: 4px solid transparent; 
                    }
                    .nav-custom-link .nav-icon { font-size: 1.15rem; margin-right: 14px; color: #6b8e6d; transition: color 0.2s ease-in-out; }
                    .nav-custom-link:hover { background-color: rgba(153, 255, 0, 0.1); color: #99ff00; }
                    .nav-custom-link:hover .nav-icon { color: #99ff00; }
                    .nav-custom-link.active { 
                        background-color: rgba(153, 255, 0, 0.15); 
                        color: #99ff00; 
                        border-left: 4px solid #FFea00; 
                        box-shadow: inset 0 0 10px rgba(153, 255, 0, 0.1); 
                    }
                    .nav-custom-link.active .nav-icon { color: #99ff00; text-shadow: 0 0 10px #99ff00; }
                    
                    .logo-box { 
                        background: linear-gradient(135deg, #1A7A38 0%, #0a3015 100%); 
                        border: 1px solid #99ff00;
                        color: #99ff00; 
                        width: 42px; height: 42px; 
                        display: flex; align-items: center; justify-content: center; 
                        border-radius: 10px; font-weight: 900; font-size: 1.1rem; flex-shrink: 0; 
                        box-shadow: 0 0 10px rgba(153, 255, 0, 0.3);
                    }
                    
                    /* 📱 RESPONSIVE MOBILE */
                    @media (max-width: 768px) {
                        .custom-sidebar { width: 75px !important; padding: 10px !important; }
                        .hide-on-mobile { display: none !important; }
                        .nav-custom-link { justify-content: center; padding: 12px 0; border-left: none !important; }
                        .nav-custom-link .nav-icon { margin-right: 0 !important; font-size: 1.4rem; }
                        .logo-box { margin-right: 0 !important; width: 40px; height: 40px; font-size: 1rem; }
                        .user-container { flex-direction: column; padding: 10px 5px !important; background: transparent !important; border: none !important; }
                        .user-container .logout-btn { margin-top: 15px; margin-left: 0 !important; }
                    }
                `}
            </style>

            <div className="custom-sidebar p-3 d-flex flex-column" style={{ width: '270px', minHeight: '100vh', position: 'sticky', top: 0 }}>
                
                <div className="d-flex align-items-center justify-content-center justify-content-md-start mb-4 mt-2 px-md-2">
                    <div className="logo-box me-md-3">FA</div>
                    <div className="hide-on-mobile">
                        <h4 className="mb-0 fw-bold" style={{ color: '#fff', letterSpacing: '-0.5px' }}>
                            FacArsn <span style={{ color: '#99ff00' }}>.</span>
                        </h4>
                        <span className="x-small fw-bold text-uppercase" style={{ color: '#88b58a', letterSpacing: '1px', fontSize: '10px' }}>Système Sécurisé</span>
                    </div>
                </div>
                
                <ul className="nav flex-column mb-auto w-100">
                    <li className="nav-item">
                        <NavLink to="/" className="nav-custom-link" title="Dashboard"><FaChartLine className="nav-icon" /> <span className="hide-on-mobile">Dashboard</span></NavLink>
                    </li>
                    
                    {!isDirection && (
                        <>
                            {canManageFacturation && (
                                <>
                                    <div className="hide-on-mobile text-uppercase fw-bold mt-3 mb-2 px-3" style={{ color: '#6b8e6d', fontSize: '11px', letterSpacing: '2px' }}>Facturation</div>
                                    <li className="nav-item"><NavLink to="/prestations" className="nav-custom-link" title="Prestations"><FaClipboardList className="nav-icon" /> <span className="hide-on-mobile">Prestations</span></NavLink></li>
                                    <li className="nav-item"><NavLink to="/clients" className="nav-custom-link" title="Base Clients"><FaUsers className="nav-icon" /> <span className="hide-on-mobile">Base Clients</span></NavLink></li>
                                    <li className="nav-item"><NavLink to="/proformas" className="nav-custom-link" title="Proformas"><FaFileInvoice className="nav-icon" /> <span className="hide-on-mobile">Proformas</span></NavLink></li>
                                    <li className="nav-item"><NavLink to="/factures" className="nav-custom-link" title="Factures Définitives"><FaEuroSign className="nav-icon" /> <span className="hide-on-mobile">Factures Définitives</span></NavLink></li>
                                    <li className="nav-item">
                                        <NavLink to="/avoirs" className="nav-custom-link" title="Factures d'Avoir">
                                            <FaFileInvoiceDollar className="nav-icon" /> <span className="hide-on-mobile">Factures d'Avoir</span>
                                        </NavLink>
                                    </li>
                                </>
                            )}

                            {canManageEncaissement && (
                                <>
                                    <div className="hide-on-mobile text-uppercase fw-bold mt-4 mb-2 px-3" style={{ color: '#6b8e6d', fontSize: '11px', letterSpacing: '2px' }}>Finance & Caisse</div>
                                    <li className="nav-item">
                                        <NavLink to="/encaissements" className="nav-custom-link" title="Encaissements"><FaMoneyBillWave className="nav-icon" /> <span className="hide-on-mobile">Encaissements</span></NavLink>
                                    </li>
                                    
                                    {canViewTresorerie && (
                                        <li className="nav-item">
                                            <NavLink to="/tresorerie" className="nav-custom-link" title="Livre de Trésorerie"><FaBook className="nav-icon" /> <span className="hide-on-mobile">Livre de Trésorerie</span></NavLink>
                                        </li>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </ul>

                <hr className="opacity-25 my-3 w-100" style={{ borderColor: '#99ff00' }} />

                <ul className="nav flex-column w-100">
                    {isSuperAdmin && (
                        <li className="nav-item">
                            <NavLink to="/admin-reset" className="nav-custom-link text-danger" style={{ color: '#ff4444' }} title="Sécurité & Reset"><FaUserShield className="nav-icon text-danger" /> <span className="hide-on-mobile">Sécurité & Reset</span></NavLink>
                        </li>
                    )}
                    {!isDirection && (
                        <li className="nav-item">
                            <NavLink to="/settings" className="nav-custom-link" title="Paramètres"><FaCog className="nav-icon" /> <span className="hide-on-mobile">Paramètres</span></NavLink>
                        </li>
                    )}
                </ul>

                <div className="d-flex align-items-center mt-3 p-2 rounded user-container" style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(153,255,0,0.3)' }}>
                    <div className="rounded-circle d-flex align-items-center justify-content-center text-dark fw-bold shadow-sm" style={{ width: '36px', height: '36px', backgroundColor: isDirection ? '#FFea00' : (isSuperAdmin ? '#ff4444' : '#99ff00'), flexShrink: 0 }}>
                        {getInitials(user?.fullName)}
                    </div>
                    <div className="flex-grow-1 overflow-hidden ms-2 hide-on-mobile">
                        <small className="d-block text-truncate fw-bold text-white" style={{ fontSize: '13px' }}>{user?.fullName || 'Direction'}</small>
                        <small className="d-block text-truncate" style={{ fontSize: '11px', color: isDirection ? '#FFea00' : (isSuperAdmin ? '#ff4444' : '#99ff00'), fontWeight: 'bold' }}>{roleLabel}</small>
                    </div>
                    <button className="btn btn-link text-danger p-1 ms-1 logout-btn" onClick={onLogout} title="Déconnexion"><FaSignOutAlt size={20} /></button>
                </div>
            </div>
        </>
    );
};

export default Sidebar;